/**
 * Modal preview for the "Export operation" feature.
 *
 * Mirrors WarPlanExportModal but with a single, unified scope (all
 * operation-mode objects + arrows).
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { toPng } from 'html-to-image';
import type { MapArrow, MapObject } from '@warplan/shared';
import type { MapBackgroundKind } from '../lib/foxhole-maps.js';
import {
  buildExportFilterItems,
  computeActiveBoundingBox,
  computeEffectiveDisabledRegions,
  getAvailableHexes,
  getItemObjectIds,
  dataUrlToBlob,
} from '../lib/war-plan-export.js';
import {
  buildOperationExportFileName,
  filterOperationArrowsForExport,
  filterOperationMarkersForExport,
} from '../lib/operation-export.js';
import { OperationExportView, type ExportWarLabel } from './OperationExportView.js';
import { ExportFilterPanel } from './ExportFilterPanel.js';
import { useOperationPlan } from '../lib/operation-plan-api.js';

export interface OperationExportModalProps {
  open: boolean;
  onClose: () => void;
  objects: ReadonlyArray<MapObject>;
  arrows: ReadonlyArray<MapArrow>;
  disabledRegionKeys: ReadonlySet<string>;
  mapBackground: MapBackgroundKind;
  warLabels: ReadonlyArray<ExportWarLabel>;
}

type Status =
  | { kind: 'idle' }
  | { kind: 'rendering' }
  | { kind: 'ready'; dataUrl: string }
  | { kind: 'error'; message: string }
  | { kind: 'empty' };

const CAPTURE_PIXEL_RATIO = 2;

export function OperationExportModal({
  open,
  onClose,
  objects,
  arrows,
  disabledRegionKeys,
  mapBackground,
  warLabels,
}: OperationExportModalProps): React.JSX.Element | null {
  const captureRef = useRef<HTMLDivElement | null>(null);
  const [step, setStep] = useState<'filter' | 'capture'>('filter');
  const [excludedIds, setExcludedIds] = useState<Set<string>>(new Set());
  const [excludedHexKeys, setExcludedHexKeys] = useState<Set<string>>(new Set());
  const [status, setStatus] = useState<Status>({ kind: 'idle' });
  const [copyState, setCopyState] = useState<'idle' | 'copied' | 'error'>('idle');

  const operationPlanQuery = useOperationPlan();

  // For operation mode, every object/arrow is exportable (no kind filter).
  const allFilterItems = useMemo(
    () =>
      open
        ? buildExportFilterItems(objects, disabledRegionKeys, warLabels, arrows)
        : [],
    [open, objects, disabledRegionKeys, warLabels, arrows],
  );

  const availableHexes = useMemo(
    () => (open ? getAvailableHexes(disabledRegionKeys) : []),
    [open, disabledRegionKeys],
  );

  const effectiveDisabledKeys = useMemo(
    () =>
      computeEffectiveDisabledRegions(
        objects,
        disabledRegionKeys,
        excludedIds,
        excludedHexKeys,
      ),
    [objects, disabledRegionKeys, excludedIds, excludedHexKeys],
  );

  const bbox = open ? computeActiveBoundingBox(effectiveDisabledKeys) : null;
  const markers = filterOperationMarkersForExport(objects, effectiveDisabledKeys, excludedIds);
  const exportArrows = filterOperationArrowsForExport(
    arrows,
    effectiveDisabledKeys,
    excludedIds,
  );
  const caption = `11ePlanner — généré le ${new Date().toLocaleString()}`;

  useEffect(() => {
    if (open) {
      setStep('filter');
      setExcludedIds(new Set());
      setExcludedHexKeys(new Set());
      setStatus({ kind: 'idle' });
      setCopyState('idle');
    } else {
      setStatus({ kind: 'idle' });
      setCopyState('idle');
    }
  }, [open]);

  useEffect(() => {
    if (!open || step !== 'capture') return;
    if (!bbox) {
      setStatus({ kind: 'empty' });
      return;
    }

    setStatus({ kind: 'rendering' });
    let cancelled = false;

    async function runCapture(): Promise<void> {
      if (cancelled) return;
      const node = captureRef.current;
      if (!node) {
        setStatus({ kind: 'error', message: 'Nœud de capture introuvable.' });
        return;
      }
      try {
        const dataUrl = await toPng(node, {
          pixelRatio: CAPTURE_PIXEL_RATIO,
          cacheBust: true,
          backgroundColor: '#0f172a',
        });
        if (!cancelled) setStatus({ kind: 'ready', dataUrl });
      } catch (err) {
        if (!cancelled) {
          const message = err instanceof Error ? err.message : 'Échec de la capture.';
          setStatus({ kind: 'error', message });
        }
      }
    }

    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        void runCapture();
      });
    });

    return () => {
      cancelled = true;
      cancelAnimationFrame(raf1);
      if (raf2) cancelAnimationFrame(raf2);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, step, objects, arrows, disabledRegionKeys, excludedIds, excludedHexKeys, mapBackground, warLabels, operationPlanQuery.data]);

  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  async function handleCopy(): Promise<void> {
    if (status.kind !== 'ready') return;
    try {
      const blob = dataUrlToBlob(status.dataUrl);
      if (typeof ClipboardItem === 'undefined') {
        throw new Error('Presse-papiers image non supporté par ce navigateur.');
      }
      await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })]);
      setCopyState('copied');
      setTimeout(() => setCopyState('idle'), 2000);
    } catch (err) {
      console.error('clipboard write failed', err);
      setCopyState('error');
      setTimeout(() => setCopyState('idle'), 3000);
    }
  }

  function handleDownload(): void {
    if (status.kind !== 'ready') return;
    const a = document.createElement('a');
    a.href = status.dataUrl;
    a.download = buildOperationExportFileName();
    document.body.appendChild(a);
    a.click();
    a.remove();
  }

  const noneIncluded =
    allFilterItems.length > 0 &&
    allFilterItems.every((i) => getItemObjectIds(i).every((id) => excludedIds.has(id)));

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Exporter l'opération"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        aria-hidden="true"
        style={{ position: 'fixed', left: -99999, top: 0, pointerEvents: 'none' }}
      >
        {step === 'capture' && bbox && (
          <div ref={captureRef}>
            <OperationExportView
              bbox={bbox}
              markers={markers}
              collisionContext={objects}
              arrows={exportArrows}
              disabledRegionKeys={effectiveDisabledKeys}
              mapBackground={mapBackground}
              operationPlan={operationPlanQuery.data ?? null}
              warLabels={warLabels}
              caption={caption}
            />
          </div>
        )}
      </div>

      <div className="flex max-h-[90vh] w-[min(96vw,1400px)] flex-col overflow-hidden rounded border border-slate-700 bg-slate-900 shadow-2xl">
        <header className="flex items-center justify-between gap-2 border-b border-slate-700 px-4 py-3">
          <h2 className="text-base font-semibold text-orange-200">
            {step === 'filter' ? 'Filtrer les éléments à exporter' : "Exporter l'opération"}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded px-2 py-1 text-sm text-slate-300 hover:bg-slate-700 hover:text-slate-100"
            aria-label="Fermer"
          >
            ✕
          </button>
        </header>

        <div className="flex-1 overflow-hidden">
          {step === 'filter' ? (
            <ExportFilterPanel
              items={allFilterItems}
              excludedIds={excludedIds}
              onToggle={(id) => {
                setExcludedIds((prev) => {
                  const next = new Set(prev);
                  if (next.has(id)) next.delete(id);
                  else next.add(id);
                  return next;
                });
              }}
              onToggleHex={(ids) => {
                setExcludedIds((prev) => {
                  const next = new Set(prev);
                  const allCurrentlyIncluded = ids.every((id) => !prev.has(id));
                  if (allCurrentlyIncluded) {
                    ids.forEach((id) => next.add(id));
                  } else {
                    ids.forEach((id) => next.delete(id));
                  }
                  return next;
                });
              }}
              onSelectAll={() => setExcludedIds(new Set())}
              onDeselectAll={() =>
                setExcludedIds(new Set(allFilterItems.flatMap((i) => [...getItemObjectIds(i)])))
              }
              availableHexes={availableHexes}
              excludedHexKeys={excludedHexKeys}
              onToggleHexKey={(key) => {
                setExcludedHexKeys((prev) => {
                  const next = new Set(prev);
                  if (next.has(key)) next.delete(key);
                  else next.add(key);
                  return next;
                });
              }}
              onSelectAllHexes={() => setExcludedHexKeys(new Set())}
              onDeselectAllHexes={() =>
                setExcludedHexKeys(new Set(availableHexes.map((h) => h.key)))
              }
            />
          ) : (
            <div className="overflow-auto bg-slate-950/60 p-4">
              {status.kind === 'rendering' && (
                <p className="text-center text-sm text-slate-400">Génération de l'image…</p>
              )}
              {status.kind === 'empty' && (
                <p className="text-center text-sm text-amber-300">
                  Aucun hexagone actif — activez au moins une région pour exporter.
                </p>
              )}
              {status.kind === 'error' && (
                <p className="text-center text-sm text-red-400">
                  Échec de la capture : {status.message}
                </p>
              )}
              {status.kind === 'ready' && (
                <img
                  src={status.dataUrl}
                  alt="Aperçu de l'opération"
                  className="mx-auto block max-h-[70vh] w-auto max-w-full rounded border border-slate-700"
                />
              )}
            </div>
          )}
        </div>

        <footer className="flex flex-wrap items-center justify-end gap-2 border-t border-slate-700 px-4 py-3">
          {copyState === 'copied' && (
            <span className="mr-auto text-xs text-emerald-300">Copié dans le presse-papiers ✓</span>
          )}
          {copyState === 'error' && (
            <span className="mr-auto text-xs text-red-400">Copie impossible.</span>
          )}
          <button
            type="button"
            onClick={onClose}
            className="rounded bg-slate-700 px-3 py-1.5 text-sm text-slate-100 hover:bg-slate-600"
          >
            Fermer
          </button>
          {step === 'filter' ? (
            <button
              type="button"
              disabled={noneIncluded || allFilterItems.length === 0}
              onClick={() => {
                setStatus({ kind: 'idle' });
                setStep('capture');
              }}
              className="rounded bg-orange-600 px-3 py-1.5 text-sm text-white hover:bg-orange-500 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Générer l'export
            </button>
          ) : (
            <>
              <button
                type="button"
                onClick={() => {
                  setStep('filter');
                  setStatus({ kind: 'idle' });
                  setCopyState('idle');
                }}
                className="rounded bg-slate-700 px-3 py-1.5 text-sm text-slate-100 hover:bg-slate-600"
              >
                ← Retour
              </button>
              <button
                type="button"
                onClick={() => {
                  void handleCopy();
                }}
                disabled={status.kind !== 'ready'}
                className="rounded bg-slate-700 px-3 py-1.5 text-sm text-slate-100 hover:bg-slate-600 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Copier dans le presse-papiers
              </button>
              <button
                type="button"
                onClick={handleDownload}
                disabled={status.kind !== 'ready'}
                className="rounded bg-orange-600 px-3 py-1.5 text-sm text-white hover:bg-orange-500 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Télécharger PNG
              </button>
            </>
          )}
        </footer>
      </div>
    </div>
  );
}
